import * as React from "react";

import { useTranslation } from "@lib/useTypedTranslation";
import { useWorldRequest } from "@lib/useWorldRequest";
import { ClusterMapContainer } from "@components/maps/clusterMap";
import { ALERT } from "@components/alertsPanel/alertMessages";
import { OptionPicker } from "@components/controls/optionPicker";
import { ChipButton, Props as ChipButtonProps } from "@components/controls/chipButton";
import { Button, Variants } from "@components/controls/button";
import { Option } from "@components/controls/optionPickerLib";
import { TimePeriodAndFilterPicker, TimePeriodPicker } from "@components/timePeriodAndFilterPicker/timePeriodAndFilterPicker";
import { useTimePeriodPicker } from "@components/timePeriodAndFilterPicker/useTimePeriodPicker";
import { useFilterPicker } from "@components/timePeriodAndFilterPicker/useFilterPicker";
import { Loading } from "@components/loading/loading";

import { getDeviceCluster, getDeviceStats } from "../../../../services/core/maps";
import { ClusterContext } from "../../maps/clusterContext";
import { useAlertNames } from "../../../../containers/core/overview/compositions/alertFilters";
import { alarmStatuses } from "../../../../services/config/alertConstants";
import { ButtonColours } from "../../../../containers/app/themes";

import "./location.css";

export const useAlertFilter = () => {
  const { state, dispatch } = React.useContext(ClusterContext);

  // Alert field filter

  const alertNames = useAlertNames();
  const alertFieldOptions = alarmStatuses.map((alertType) => {
    return {
      value: alertType,
      name: alertNames[alertType]
    };
  });
  const alertFieldOption = alertFieldOptions.find((option) => option.value === state?.alertField) || null;

  // Alert values filter

  const allAlertValues = [ALERT.yellow, ALERT.red] as const;
  const alertValueButtonProps = allAlertValues.map((alertValue): ChipButtonProps & { alertValue: string } => {
    const active = state?.alertValues?.includes(alertValue);

    const disabled = alertFieldOption === null ||
      (alertValue === ALERT.yellow && alertFieldOption?.value === 'deviceTimeErrorStatus');

    const icon = alertValue === ALERT.red ?
      <i data-id='problem-status-icon' className={`fa fa-times-circle circle_red`} />
      :
      <i data-id='warning-status-icon' className={`fa fa-exclamation-triangle circle_orange enhanced-warning-icon`} />;

    const onClick = () => {
      dispatch({ type: 'TOGGLE_ALERT_VALUE', payload: alertValue });
    };

    return { active, disabled, icon, onClick, alertValue, dataId: `alert-value-${alertValue}` };
  });

  return {
    alertFieldPickerProps: {
      current: alertFieldOption,
      options: alertFieldOptions,
      onSelect: (option: Option<any>) => {
        dispatch({ type: 'SET_ALERT_FIELD', payload: option?.value });
      },
      inactive: alertFieldOption === null,
    },
    alertValueButtonProps,
    resetButtonProps: {
      disabled: alertFieldOption === null,
      onClick: () => dispatch({ type: 'SET_ALERT_FIELD', payload: undefined })
    }
  };
};

export const useLocationRequests = () => {
  const { state } = React.useContext(ClusterContext);

  const fetchDeviceStats = React.useCallback(() => {
    return getDeviceStats({
      alertField: state?.alertField,
      alertValues: state?.alertValues,
      from: state?.from,
      to: state?.to,
      groups: state?.groups,
      homeLocations: state?.homeLocations,
      zoneId: state?.zoneId,
    });
  }, [state]);

  const deviceStatsRequest = useWorldRequest(fetchDeviceStats, { initialLoading: true });

  const fetchMapData = React.useCallback((boundingLatLong: string, geotilePrecision: number) => {
    return getDeviceCluster({
      boundingLatLong,
      geotilePrecision,
      alertField: state?.alertField,
      alertValues: state?.alertValues,
      from: state?.from,
      to: state?.to,
      groups: state?.groups,
      homeLocations: state?.homeLocations,
      zoneId: state?.zoneId,
    });
  }, [state]);

  return { deviceStatsRequest, fetchMapData };
};

export const useFilters = () => {
  const { dispatch } = React.useContext(ClusterContext);

  const twoHours = 2 / 24;
  const timePeriodPickerProps = useTimePeriodPicker({ timePeriods: [30, 7, 2, 1, twoHours], enableAllTimeOption: true });

  React.useEffect(() => {
    dispatch({ type: 'SET_TIME_PERIOD', payload: timePeriodPickerProps.timePeriod?.value });
  }, [timePeriodPickerProps.timePeriod, dispatch]);

  const filterPickerProps = useFilterPicker();

  React.useEffect(() => {
    dispatch({ type: 'SET_FILTER', payload: filterPickerProps.filter });
  }, [filterPickerProps.filter, dispatch]);

  return { timePeriodPickerProps, filterPickerProps };
};

export interface LocationTabProps {
  /** set by the tab panel via passSelectedToChild - false while the tab is hidden */
  selected?: boolean
}

export const LocationTab = ({ selected }: LocationTabProps) => {
  const { t } = useTranslation(['maps', 'translation', 'filters']);
  const { alertFieldPickerProps, alertValueButtonProps, resetButtonProps } = useAlertFilter();
  const { timePeriodPickerProps, filterPickerProps } = useFilters();
  const { deviceStatsRequest, fetchMapData } = useLocationRequests();

  return <>
    <div className="core-maps_header">
      <div className="core-maps_headings">
        <h2>{t('LOCATION_SUBHEADING', { ns: 'maps' })}</h2>
      </div>
      <div className="core-maps_filters">

        <div className="core-maps_time-and-estate-filters">
          <TimePeriodAndFilterPicker
            {...filterPickerProps}
            {...timePeriodPickerProps}
            className='core-maps_time-and-estate-filters'
          />
        </div>
        <div className="core-maps-options-filters">
          <div className="alert-filters">
            <label>{t('ALERT', { ns: 'filters' })}
              <OptionPicker
                id="status-field-picker"
                selectText={t('ALERT_TYPES_FILTER', { ns: 'filters' })}
                {...alertFieldPickerProps}
              />
            </label>
            <div className="radio-buttons alert-values">
              {alertValueButtonProps.map(({ alertValue, ...rest }) => <ChipButton
                key={alertValue}
                {...rest}
              />)}
            </div>
            <Button
              className="alert-clear"
              // iconStyle="fa fa-times-circle"
              iconStyle="custom-icon-filter-slash"
              colour={ButtonColours.blue}
              variant={Variants.minimal}
              {...resetButtonProps}
            />
          </div>
        </div>
      </div>
    </div>
    <Loading isLoading={filterPickerProps.optionsLoading} transparentOverlay={false}>
      <ClusterMapContainer
        deviceStatsRequest={deviceStatsRequest}
        fetchMapData={fetchMapData}
        selected={selected}
      />
    </Loading>
  </>;
};
